import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const AppAccountDelete = () => {
  // Check for utm=mobile query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const isMobile = urlParams.get('utm') === 'mobile';

  const steps = [
    {
      number: 1,
      title: "Open MetroRahi App",
      description: "Launch the MetroRahi app on your device"
    },
    {
      number: 2,
      title: "Navigate to Profile",
      description: "Tap on the Menu icon (☰) located at the top-left or bottom of the screen"
    },
    {
      number: 3,
      title: "Access My Profile",
      description: "Select 'My Profile' from the menu options"
    },
    {
      number: 4,
      title: "Delete Account",
      description: "Scroll down and tap on 'Delete My Account' option"
    },
    {
      number: 5,
      title: "Confirm Deletion",
      description: "Review the information and confirm your account deletion. This action is permanent and cannot be undone."
    }
  ];

  return (
    <div className="policy-page app-policy">
      <div className="policy-container">
        <div className="policy-document">
          {/* Header */}
          <motion.div
            className="policy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {!isMobile && (
              <Link to="/" className="back-button">
                <ArrowLeft className="back-icon" />
                <span>Back</span>
              </Link>
            )}
            
            <div className="policy-title-section">
              <h1>Delete Your Account</h1>
              <p>How to Delete Your MetroRahi Account</p>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            className="policy-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="policy-text">
              <p className="bold-text">
                Follow these simple steps to permanently delete your MetroRahi account and all associated data.
              </p>

              <br />

              {/* Steps Section */}
              <div className="account-delete-steps">
                {steps.map((step, index) => (
                  <motion.div
                    key={step.number}
                    className="delete-step-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  >
                    <div className="step-number">{step.number}</div>
                    <div className="step-content">
                      <h3>{step.title}</h3>
                      <p>{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <br />

              {/* Screenshot Section */}
              <div className="screenshot-section">
                <h2>Screenshot Guide</h2>
                <p>Below is a visual guide showing the account deletion option in the app:</p>
                <div className="screenshot-container">
                  <img 
                    src="/images/account_delete.png" 
                    alt="Account deletion screenshot" 
                    className="account-delete-screenshot"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  {/* <div className="screenshot-placeholder" style={{ display: 'none' }}>
                    <Trash2 className="placeholder-icon" />
                    <p>Screenshot will be displayed here</p>
                    <p className="placeholder-note">Please add the screenshot at /public/images/account_delete.PNG</p>
                  </div> */}
                </div>
              </div>

              <br />

              {/* Important Notes */}
              <div className="important-notes">
                <h2>Important Information</h2>
                <ul>
                  <li>Account deletion is permanent and cannot be undone</li>
                  <li>All your data including routes, preferences, and history will be permanently deleted</li>
                  <li>You will need to create a new account if you wish to use MetroRahi again</li>
                  <li>Any active subscriptions will need to be cancelled separately through your app store</li>
                  <li>Deletion may take up to 24-48 hours to complete</li>
                </ul>
              </div>

              <br />

              {/* Support Section */}
              <div className="support-section">
                <h2>Need Help?</h2>
                <p>If you're having trouble deleting your account or have questions, please contact our support team:</p>
                <p className="bold-text">
                  📧 <strong>Email:</strong> support.metrorahi@resiara.com
                </p>
              </div>
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            className="policy-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="company-info">
              <p>Brought to you by <a href="https://resiara.com" target="_blank" rel="noopener noreferrer" className="company-link">Resiara Software Pvt. Ltd.</a></p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AppAccountDelete;

